import {
	ApiService,
	evaluateAppUpdate,
	Platform,
	type AppBuildStampModel,
	type DeployedVersionFileModel,
} from '@supply-mover-2-0/core';

/**
 * Web update detection and application.
 *
 * The build stamps a version into the bundle and deploys a `version.json`
 * beside it (see the app's vite version plugin). This service polls that
 * file — every five minutes, plus on window focus and network reconnect,
 * the moments a long-idle tab comes back to life — and compares via
 * `evaluateAppUpdate` (libs/core, unit-tested).
 *
 * When a new build is available the host app shows a banner, and the update
 * applies itself at a QUIET moment: a reload timer is armed, and elapsing
 * requires the last API request (ApiService.lastRequestAt) to be at least
 * QUIET_MS old — network activity is the signal that someone is mid-task,
 * so an active user is never interrupted (the mechanism our previous
 * platform used for years). A build below the deployed file's
 * `minSupportedBuildTime` reloads immediately instead.
 *
 * Disabled on native shells (their code ships via the app stores) and in
 * dev (Vite serves from source). A sessionStorage flag survives the reload
 * so the host can toast "updated" on the way back in, and a reload guard
 * ensures a stubbornly-cached tab never reload-loops.
 */

export interface AppUpdateStateModel {
	updateAvailable: boolean;
	/** True while an automatic quiet-moment reload is pending. */
	reloadPending: boolean;
}

type Listener = (state: AppUpdateStateModel) => void;

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const QUIET_MS = 10 * 1000;
const QUIET_POLL_MS = 1000;
/** Minimum spacing between focus-triggered checks. */
const FOCUS_CHECK_DEBOUNCE_MS = 30 * 1000;
const RELOADED_FLAG = 'workd:app-update:reloaded';
const RELOAD_GUARD = 'workd:app-update:reload-guard';

class AppUpdateServiceClass {
	private _running: AppBuildStampModel | null = null;
	private _versionUrl = '/version.json';
	private _state: AppUpdateStateModel = {
		updateAvailable: false,
		reloadPending: false,
	};
	private readonly _listeners = new Set<Listener>();
	private _checkTimer: ReturnType<typeof setInterval> | null = null;
	private _quietTimer: ReturnType<typeof setInterval> | null = null;
	private _updateDetectedAt = 0;
	private _lastCheckAt = 0;
	private _started = false;

	/**
	 * Start watching. Call once from the app shell with the stamp the build
	 * baked in; no-ops without one (dev) or on a native shell.
	 */
	public start(
		running: AppBuildStampModel | null,
		options?: { versionUrl?: string },
	): void {
		if (this._started || running == null || Platform.isNative()) {
			return;
		}
		this._started = true;
		this._running = running;
		if (options?.versionUrl != null) {
			this._versionUrl = options.versionUrl;
		}

		this._checkTimer = setInterval(() => {
			void this.checkNow();
		}, CHECK_INTERVAL_MS);
		window.addEventListener('focus', this._onWake);
		window.addEventListener('online', this._onWake);

		// First check shortly after boot — a tab restored from the browser's
		// cache can already be stale the moment it opens.
		setTimeout(() => {
			void this.checkNow();
		}, 5000);
	}

	/**
	 * Whether the previous page load was this service applying an update.
	 * Deliberately does NOT clear the reload guard: a reload that came back
	 * still-stale must not re-arm itself. The guard clears only when a check
	 * confirms the running build is current (see checkNow).
	 */
	public consumeJustUpdatedFlag(): boolean {
		try {
			if (sessionStorage.getItem(RELOADED_FLAG) != null) {
				sessionStorage.removeItem(RELOADED_FLAG);
				return true;
			}
		} catch {
			// Storage unavailable (private mode) — nothing to consume.
		}
		return false;
	}

	public getState(): AppUpdateStateModel {
		return this._state;
	}

	public subscribe(listener: Listener): () => void {
		this._listeners.add(listener);
		return () => {
			this._listeners.delete(listener);
		};
	}

	/** The banner's Refresh button — user asked, apply now. */
	public applyNow(): void {
		this._reload();
	}

	public async checkNow(): Promise<void> {
		if (this._running == null) {
			return;
		}
		this._lastCheckAt = Date.now();

		let deployed: DeployedVersionFileModel | null = null;
		try {
			const response = await fetch(`${this._versionUrl}?t=${Date.now()}`, {
				cache: 'no-store',
			});
			if (!response.ok) {
				return;
			}
			deployed = (await response.json()) as DeployedVersionFileModel;
		} catch {
			// Offline / CDN hiccup — never treat a failed check as an update.
			return;
		}

		const verdict = evaluateAppUpdate(this._running, deployed);
		if (!verdict.updateAvailable) {
			// Confirmed current — the one place the reload-loop guard clears,
			// so the NEXT deploy can auto-apply again.
			try {
				sessionStorage.removeItem(RELOAD_GUARD);
			} catch {
				// Nothing to clear if storage is unavailable.
			}
			return;
		}

		if (verdict.forceUpdate) {
			this._reload();
			return;
		}

		if (!this._state.updateAvailable) {
			this._updateDetectedAt = Date.now();
			this._setState({ updateAvailable: true, reloadPending: true });
			this._armQuietReload();
		}
	}

	private readonly _onWake = (): void => {
		if (Date.now() - this._lastCheckAt > FOCUS_CHECK_DEBOUNCE_MS) {
			void this.checkNow();
		}
	};

	/**
	 * Apply the pending update once the app has been network-quiet for
	 * QUIET_MS: each API request implicitly pushes the reload back, so it
	 * fires only between tasks, never during one.
	 */
	private _armQuietReload(): void {
		if (this._quietTimer != null) {
			return;
		}
		this._quietTimer = setInterval(() => {
			const now = Date.now();
			const quietSince = Math.max(
				ApiService.lastRequestAt,
				this._updateDetectedAt,
			);
			if (now - quietSince >= QUIET_MS) {
				this._reload();
			}
		}, QUIET_POLL_MS);
	}

	private _reload(): void {
		try {
			// One automatic reload per detected version: if the tab comes back
			// STILL stale (index.html pinned by an aggressive cache), don't
			// loop — leave the banner up so the user refreshes by hand.
			const guard = sessionStorage.getItem(RELOAD_GUARD);
			if (guard === this._running?.version) {
				this._disarmQuietReload();
				this._setState({ updateAvailable: true, reloadPending: false });
				return;
			}
			sessionStorage.setItem(RELOAD_GUARD, this._running?.version ?? '');
			sessionStorage.setItem(RELOADED_FLAG, '1');
		} catch {
			// Storage unavailable — reload anyway; worst case no toast.
		}
		window.location.reload();
	}

	private _disarmQuietReload(): void {
		if (this._quietTimer != null) {
			clearInterval(this._quietTimer);
			this._quietTimer = null;
		}
	}

	private _setState(state: AppUpdateStateModel): void {
		this._state = state;
		for (const listener of this._listeners) {
			listener(state);
		}
	}
}

export const AppUpdateService = new AppUpdateServiceClass();
